import {Action} from '@reduxjs/toolkit'
import {AreaModel} from '../models/AreaModel'

export interface ActionWithPayload<T> extends Action {
  payload?: T
}

export const actionTypes = {
  AreasRequested: '[Request Areas] Action',
  AreasLoaded: '[Load Areas] Areas API',
  AreasFailed: '[Load Areas] Areas API Failed',
  AreaRequested: '[Request Area] Action',
  AreaLoaded: '[Load Area] Area API',
  AreaFailed: '[Load Area] Area API Failed',
  AreaCreated: '[Create Area] Action',
  AreaUpdated: '[Update Area] Action',
  AreaDeleted: '[Delete Area] Action',
}

const initialAreaState: IAreaState = {
  areas: [],
  area: undefined,
  loading: false,
  error: undefined,
}

export interface IAreaState {
  areas: AreaModel[]
  area?: AreaModel
  loading: boolean
  error?: string
}

export const reducer = (state: IAreaState = initialAreaState, action: ActionWithPayload<IAreaState>) => {
  switch (action.type) {
    case actionTypes.AreasRequested: {
      return {...state, loading: true, error: undefined}
    }

    case actionTypes.AreasLoaded: {
      const areas = action.payload?.areas || []
      return {...state, areas, loading: false, error: undefined}
    }

    case actionTypes.AreasFailed: {
      const error = action.payload?.error
      return {...state, loading: false, error}
    }

    case actionTypes.AreaRequested: {
      return {...state, loading: true, error: undefined}
    }

    case actionTypes.AreaLoaded: {
      const area = action.payload?.area
      return {...state, area, loading: false, error: undefined}
    }

    case actionTypes.AreaFailed: {
      const error = action.payload?.error
      return {...state, loading: false, error}
    }

    case actionTypes.AreaCreated: {
      const area = action.payload?.area
      if (area) {
        return {...state, areas: [...state.areas, area], loading: false}
      }
      return state
    }

    case actionTypes.AreaUpdated: {
      const area = action.payload?.area
      if (area) {
        const updatedAreas = state.areas.map(a => (a.id === area.id ? area : a))
        return {...state, areas: updatedAreas, area, loading: false}
      }
      return state
    }

    case actionTypes.AreaDeleted: {
      const areaId = action.payload?.area?.id
      if (areaId) {
        const filteredAreas = state.areas.filter(a => a.id !== areaId)
        return {...state, areas: filteredAreas, loading: false}
      }
      return state
    }

    default:
      return state
  }
}

export const actions = {
  requestAreas: () => ({
    type: actionTypes.AreasRequested,
  }),
  fulfillAreas: (areas: AreaModel[]) => ({
    type: actionTypes.AreasLoaded,
    payload: {areas},
  }),
  failAreas: (error: string) => ({
    type: actionTypes.AreasFailed,
    payload: {error},
  }),
  requestArea: () => ({
    type: actionTypes.AreaRequested,
  }),
  fulfillArea: (area: AreaModel) => ({
    type: actionTypes.AreaLoaded,
    payload: {area},
  }),
  failArea: (error: string) => ({
    type: actionTypes.AreaFailed,
    payload: {error},
  }),
  createArea: (area: AreaModel) => ({
    type: actionTypes.AreaCreated,
    payload: {area},
  }),
  updateArea: (area: AreaModel) => ({
    type: actionTypes.AreaUpdated,
    payload: {area},
  }),
  deleteArea: (area: AreaModel) => ({
    type: actionTypes.AreaDeleted,
    payload: {area},
  }),
}
